/**
 * ============================================================================
 * PHONE VALIDATION UTILITIES - CAMEROON FOCUSED
 * ============================================================================
 * 
 * Phone number validation and formatting for Cameroon and other African countries
 */

/**
 * Cameroon phone number formats:
 * - Mobile: 9 digits (6XX XXX XXX or 6XX XXX XXX)
 * - Format: +237 6XX XXX XXX
 * - Common prefixes: 6, 7, 8, 9
 */

export const CAMEROON_PHONE_REGEX = /^(\+237|237)?[6-9]\d{8}$/;
export const CAMEROON_PHONE_FORMAT = /^(\+237|237)?([6-9]\d{2})(\d{3})(\d{3})$/;

/**
 * Format phone number for display
 */
export const formatPhoneNumber = (phone, countryCode = '+237') => {
  if (!phone) return '';
  
  // Remove all non-digits
  let cleaned = phone.replace(/\D/g, '');
  
  // Remove country code if present
  if (cleaned.startsWith('237')) {
    cleaned = cleaned.slice(3);
  }
  
  // Format Cameroon number: 6XX XXX XXX
  if (cleaned.length === 9 && /^[6-9]/.test(cleaned)) {
    return `${countryCode} ${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`;
  }
  
  return phone;
};

/**
 * Validate Cameroon phone number
 */
export const validateCameroonPhone = (phone) => {
  if (!phone) return { valid: false, error: 'Phone number is required' };
  
  // Remove spaces and format
  const cleaned = phone.replace(/\s/g, '').replace(/^\+/, '');
  
  // Check if starts with 237
  if (cleaned.startsWith('237')) {
    const number = cleaned.slice(3);
    if (number.length === 9 && /^[6-9]\d{8}$/.test(number)) {
      return { valid: true, formatted: `+237${number}` };
    }
  }
  
  // Check if it's just the 9 digits
  if (cleaned.length === 9 && /^[6-9]\d{8}$/.test(cleaned)) {
    return { valid: true, formatted: `+237${cleaned}` };
  }
  
  return { 
    valid: false, 
    error: 'Please enter a valid Cameroon phone number (9 digits starting with 6, 7, 8, or 9)' 
  };
};

/**
 * Get phone number validation rules by country code
 */
export const getPhoneValidation = (countryCode) => {
  const rules = {
    '+237': { // Cameroon
      pattern: /^[6-9]\d{8}$/,
      length: 9,
      placeholder: '6XX XXX XXX',
      example: '677123456',
      error: 'Enter 9 digits starting with 6, 7, 8, or 9',
    },
    '+254': { // Kenya
      pattern: /^[17]\d{8}$/,
      length: 9,
      placeholder: '712345678',
      example: '712345678',
      error: 'Enter 9 digits starting with 1 or 7',
    },
    '+256': { // Uganda
      pattern: /^[0-9]\d{8}$/,
      length: 9,
      placeholder: '712345678',
      example: '712345678',
      error: 'Enter 9 digits',
    },
    '+255': { // Tanzania
      pattern: /^[67]\d{8}$/,
      length: 9,
      placeholder: '712345678',
      example: '712345678',
      error: 'Enter 9 digits starting with 6 or 7',
    },
  };
  
  return rules[countryCode] || {
    pattern: /^\d{9,10}$/,
    length: 9,
    placeholder: 'Enter phone number',
    example: '123456789',
    error: 'Enter a valid phone number',
  };
};

/**
 * Format phone input as user types
 */
export const formatPhoneInput = (value, countryCode = '+237') => {
  // Remove all non-digits
  let cleaned = value.replace(/\D/g, '');
  
  // Remove country code if user typed it
  if (cleaned.startsWith('237')) {
    cleaned = cleaned.slice(3);
  }
  
  // Limit to 9 digits for Cameroon
  if (countryCode === '+237') {
    cleaned = cleaned.slice(0, 9);
    
    // Format: 6XX XXX XXX
    if (cleaned.length > 3) {
      return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)}${cleaned.length > 6 ? ' ' + cleaned.slice(6) : ''}`;
    }
  }
  
  return cleaned;
};

/**
 * Get full international phone number
 */
export const getFullPhoneNumber = (phone, countryCode = '+237') => {
  const cleaned = phone.replace(/\D/g, '');
  
  if (cleaned.startsWith('237')) {
    return `+${cleaned}`;
  }
  
  return `${countryCode}${cleaned}`;
};
